import React, { useMemo, useState } from "react";
import { Input, Select, DatePicker, InputNumber, Checkbox, Form } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import moment from "moment";
import { regionOptions, regionName } from "../../helper/regions";
import { dialOptions, splitPhone, joinPhone } from "../../helper/dialCodes";
import { CURRENCY_SYMBOLS, CURRENCY_OPTIONS, formatMoney } from "./employeeUi";
import { getIn, maskValue } from "./employeeSchema";

// ---------- the edit side: one antd Form.Item per schema field ----------

// Date fields hold "YYYY-MM-DD" text (or an ISO string for isodate) in the form state.
const dateProps = (iso) => ({
  getValueProps: (v) => ({ value: v ? moment(v) : null }),
  normalize: (v) => (v ? (iso ? v.toISOString() : v.format("YYYY-MM-DD")) : ""),
});

// A phone number with a country code dropdown. The form value is one text, for example "+852 91234567".
const PhoneInput = ({ value, onChange, defaultIso }) => {
  const options = useMemo(() => dialOptions(), []);
  const { iso: parsedIso, number } = splitPhone(value, defaultIso);
  const [pickedIso, setPickedIso] = useState(undefined);
  const iso = parsedIso || pickedIso || defaultIso;
  const emit = (nextIso, nextNumber) => onChange && onChange(nextNumber ? joinPhone(nextIso, nextNumber) : "");
  return (
    <Input.Group compact>
      <Select
        value={iso}
        placeholder="Code"
        showSearch
        optionFilterProp="label"
        style={{ width: 128 }}
        dropdownMatchSelectWidth={false}
        optionLabelProp="short"
        options={options.map((o) => ({ ...o, short: `${o.value} +${o.dial}` }))}
        onChange={(v) => { setPickedIso(v); emit(v, number); }}
      />
      <Input
        value={number}
        placeholder="Phone number"
        inputMode="tel"
        style={{ width: "calc(100% - 128px)" }}
        onChange={(e) => emit(iso, e.target.value.replace(/[^\d\s-]/g, ""))}
      />
    </Input.Group>
  );
};

export const FieldItem = ({ field, lookups, region }) => {
  const form = Form.useFormInstance();
  const formRegion = Form.useWatch("region", form);
  const REGIONS = useMemo(() => regionOptions(), []);
  const { type, label, help, required } = field;
  const common = { name: field.path, label, extra: help, rules: required ? [{ required: true, message: `Enter the ${label.toLowerCase()}` }] : undefined };

  if (type === "phone") return <Form.Item {...common}><PhoneInput defaultIso={formRegion || undefined} /></Form.Item>;
  if (type === "textarea") return <Form.Item {...common}><Input.TextArea rows={3} maxLength={2000} /></Form.Item>;
  if (type === "select") return <Form.Item {...common}><Select allowClear options={field.options} /></Form.Item>;
  if (type === "date") return <Form.Item {...common} {...dateProps(false)}><DatePicker style={{ width: "100%" }} /></Form.Item>;
  if (type === "isodate") return <Form.Item {...common} {...dateProps(true)}><DatePicker style={{ width: "100%" }} /></Form.Item>;
  if (type === "number") return <Form.Item {...common}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item>;
  if (type === "checkbox") return <Form.Item name={field.path} valuePropName="checked" extra={help}><Checkbox>{label}</Checkbox></Form.Item>;
  if (type === "region") return <Form.Item {...common}><Select allowClear showSearch optionFilterProp="label" placeholder="Select a country" options={REGIONS} /></Form.Item>;
  if (type === "currency") return <Form.Item {...common}><Select options={CURRENCY_OPTIONS} /></Form.Item>;
  if (type === "money") {
    return (
      <Form.Item {...common}>
        <InputNumber
          // keyed on the currency so the symbol updates immediately when the currency changes
          key={region}
          style={{ width: "100%" }}
          min={0}
          formatter={(value) => `${CURRENCY_SYMBOLS[region] || ""} ${value}`.replace(/\B(?=(\d{3})+(?!\d)(?!\D))/g, ",")}
          parser={(value) => value.replace(/[^\d.]/g, "")}
        />
      </Form.Item>
    );
  }
  if (type.startsWith("ref:")) {
    const list = lookups[type.slice(4)] || [];
    return <Form.Item {...common}><Select allowClear showSearch optionFilterProp="label" options={list.map((x) => ({ value: x._id, label: x.name }))} /></Form.Item>;
  }
  return <Form.Item {...common}><Input disabled={field.readOnly} /></Form.Item>;
};

// ---------- the read side: label + value, sensitive values masked ----------

// A masked value with an eye button to reveal it (admins only ever receive these values).
const Sensitive = ({ value }) => {
  const [shown, setShown] = useState(false);
  if (!value) return <span style={{ color: "#9CA3AF" }}>Not set</span>;
  return (
    <span>
      {shown ? value : maskValue(value)}{" "}
      <span style={{ cursor: "pointer", color: "#6B7280" }} onClick={() => setShown(!shown)} title={shown ? "Hide" : "Show"}>
        {shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
      </span>
    </span>
  );
};

const displayOf = (field, employee) => {
  const v = getIn(employee, field.path);
  if (field.type === "checkbox") return v ? "Yes" : "No";
  if (v === undefined || v === null || v === "") return null;
  if (field.type === "region") return `${regionName(v)} (${v})`;
  if (field.type === "isodate") return moment(v).format("DD MMM YYYY");
  if (field.type === "date") return moment(v, "YYYY-MM-DD").format("DD MMM YYYY");
  if (field.type === "money") return formatMoney(v, employee.salaryCurrency);
  if (field.type.startsWith("ref:")) return v?.name || null;
  if (field.type === "number") return String(v);
  return String(v);
};

export const FieldValue = ({ field, employee }) => {
  let content;
  if (field.sensitive) content = <Sensitive value={getIn(employee, field.path)} />;
  else if (field.path[0] === "workingHours" && employee.workingHours) content = `${employee.workingHours.name} · ${employee.workingHours.startTime} – ${employee.workingHours.endTime}`;
  else content = displayOf(field, employee);
  return (
    <div>
      <div style={{ color: "#6B7280", fontSize: 12, marginBottom: 2 }}>{field.label}</div>
      <div style={{ fontWeight: 500, minHeight: 20, wordBreak: "break-word" }}>{content || <span style={{ color: "#9CA3AF", fontWeight: 400 }}>Not set</span>}</div>
    </div>
  );
};
